import { useEffect, useState } from "react";
import { api, MONTHS_IT, LOGO } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, LayoutDashboard, Lightbulb } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { getRef, getSrc } from "@/lib/ref";

const sexes = [
  { v: "femmina", label: "Femmina", color: "#C87D75", bg: "#F9ECE9" },
  { v: "maschio", label: "Maschio", color: "#4A7496", bg: "#EBF3F8" },
  { v: "sorpresa", label: "Sorpresa", color: "#4E7363", bg: "#EAF0EC" },
];
const Y = new Date().getFullYear();
const yearsFor = (born) => (born ? [Y - 2, Y - 1, Y] : [Y, Y + 1]);
const monthsOld = (d) => {
  const now = new Date();
  return Math.max(0, (now.getFullYear() - d.getFullYear()) * 12 + now.getMonth() - d.getMonth() - (now.getDate() < d.getDate() ? 1 : 0));
};

function formatErr(detail) {
  if (!detail) return "Qualcosa è andato storto. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg).join(" ");
  return String(detail);
}

const InfoPill = ({ show, testId, children }) => (
  <AnimatePresence>
    {show && (
      <motion.div data-testid={testId} initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
        className="flex gap-2.5 rounded-2xl bg-[#FDF6E7] border border-[#F1E2BF] px-3.5 py-2.5 text-[12.5px] leading-snug text-[#5C534E]">
        <Lightbulb size={16} className="shrink-0 mt-0.5 text-[#C29638]" />
        <span><strong className="text-[#A87F2A]">Lo sapevi che…</strong> {children}</span>
      </motion.div>
    )}
  </AnimatePresence>
);

export default function Onboarding({ open }) {
  const { saveProfile } = useProfile();
  const now = new Date();
  const [mode, setMode] = useState("register"); // register | login
  useEffect(() => {
    if (open && localStorage.getItem("mb_onb_mode") === "login") {
      localStorage.removeItem("mb_onb_mode");
      setMode("login");
    }
  }, [open]);
  const [loading, setLoading] = useState(false);

  // register form
  const [form, setForm] = useState({
    name: "", email: "", whatsapp: "", city: "",
    day: "", month: String(now.getMonth() + 1), year: String(now.getFullYear()),
    sex: "sorpresa", est_weight: "", est_length: "", password: "", baby_name: "",
    born: false, cur_weight: "", cur_length: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // login state
  const [login, setLogin] = useState({ email: "", password: "" });
  const [codeMode, setCodeMode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [resetMode, setResetMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const requestReset = async () => {
    if (!login.email) { toast.error("Inserisci la tua email"); return; }
    setLoading(true);
    try {
      await api.post("/mother/request-password-reset", { email: login.email });
      setResetSent(true);
      toast.success("Ti abbiamo inviato un codice per reimpostare la password 🤍");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (!resetCode || !newPassword) { toast.error("Inserisci codice e nuova password"); return; }
    if (newPassword.length < 6) { toast.error("La password deve avere almeno 6 caratteri"); return; }
    setLoading(true);
    try {
      await api.post("/mother/reset-password", { email: login.email, code: resetCode, new_password: newPassword });
      toast.success("Password aggiornata! Ora puoi accedere 🤍");
      setResetMode(false); setResetSent(false); setResetCode(""); setNewPassword("");
      setLogin((l) => ({ ...l, password: "" }));
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const afterAuth = (profile) => {
    saveProfile(profile);
    toast.success(`Bentornata, ${profile.name?.split(" ")[0] || ""} 🤍`);
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.whatsapp || !form.city || !form.day) {
      toast.error("Compila i campi obbligatori");
      return;
    }
    const date = new Date(Number(form.year), Number(form.month) - 1, Number(form.day));
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (form.born && date > today) {
      toast.error("La data di nascita non può essere nel futuro");
      return;
    }
    if (!form.born && date < today) {
      toast.error("Questa data è già passata: se il bimbo è già nato, scegli \"È già nato\"");
      return;
    }
    if (!form.sex || (form.born && form.sex === "sorpresa")) {
      toast.error("Indica se è una bimba o un bimbo");
      return;
    }
    if (form.whatsapp.replace(/\D/g, "").length < 8) {
      toast.error("Inserisci un numero WhatsApp valido");
      return;
    }
    if (!form.password || form.password.length < 6) {
      toast.error("Scegli una password di almeno 6 caratteri");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name, email: form.email, whatsapp: form.whatsapp, city: form.city,
        due_date: { day: Number(form.day), month: Number(form.month), year: Number(form.year) },
        sex: form.sex,
        est_weight: form.est_weight ? Number(form.est_weight) : null,
        est_length: form.est_length ? Number(form.est_length) : null,
        baby_name: form.baby_name.trim() || null,
        password: form.password,
        ref: getRef(),
        src: getSrc(),
      };
      const { data } = await api.post("/onboarding", payload);
      if (form.born && (form.cur_weight || form.cur_length)) {
        try {
          const { data: g } = await api.post(`/profile/${data.id}/growth`, {
            month: monthsOld(date),
            weight: form.cur_weight ? Number(form.cur_weight) : null,
            length: form.cur_length ? Number(form.cur_length) : null,
          });
          data.growth_records = g.growth_records;
        } catch (err) {
          console.error("growth record not saved", err);
        }
      }
      saveProfile(data);
      toast.success("Profilo creato! Ti abbiamo inviato una email 🤍");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    if (!login.email || !login.password) { toast.error("Inserisci email e password"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/mother/login", login);
      afterAuth(data.profile);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const requestCode = async () => {
    if (!login.email) { toast.error("Inserisci la tua email"); return; }
    setLoading(true);
    try {
      await api.post("/mother/request-code", { email: login.email });
      setCodeSent(true);
      toast.success("Ti abbiamo inviato un codice via email 🤍");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    if (!code) { toast.error("Inserisci il codice"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/mother/verify-code", { email: login.email, code });
      afterAuth(data.profile);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        data-testid="onboarding-modal"
        className="sm:max-w-lg bg-[#FAF8F5] border-[#EAE3DC] max-h-[92vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="mx-auto mb-3 flex items-center justify-center">
            <img src={LOGO} alt="Minime Baby" className="h-14 w-auto object-contain" />
          </div>
          <DialogTitle className="text-center font-serif text-3xl text-[#2D2825]">
            {mode === "register" ? (form.born ? "Benvenuta, mamma" : "Benvenuta, futura mamma") : "Bentornata"}
          </DialogTitle>
          <DialogDescription className="text-center text-[#8C7F78]">
            {mode === "register"
              ? "Crea il tuo profilo: consigli su misura, 15% di sconto subito e 20% dopo le 7 lezioni."
              : "Accedi al tuo profilo con email e password o con un codice."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex rounded-full bg-white border border-[#EAE3DC] p-1 mt-1">
          <button data-testid="tab-register" onClick={() => setMode("register")}
            className="flex-1 rounded-full py-2 text-sm font-display transition-colors"
            style={{ background: mode === "register" ? "#2D2825" : "transparent", color: mode === "register" ? "#fff" : "#5C534E" }}>
            Registrati
          </button>
          <button data-testid="tab-login" onClick={() => setMode("login")}
            className="flex-1 rounded-full py-2 text-sm font-display transition-colors"
            style={{ background: mode === "login" ? "#2D2825" : "transparent", color: mode === "login" ? "#fff" : "#5C534E" }}>
            Accedi
          </button>
        </div>

        {mode === "register" ? (
          <form onSubmit={submitRegister} className="space-y-4 mt-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[#5C534E]">Nome *</Label>
                <Input data-testid="onboarding-name" value={form.name}
                  onChange={(e) => set("name", e.target.value)} placeholder="Il tuo nome"
                  className="rounded-xl bg-white border-[#EAE3DC]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#5C534E]">Email *</Label>
                <Input data-testid="onboarding-email" type="email" value={form.email}
                  onChange={(e) => set("email", e.target.value)} placeholder="nome@email.it"
                  className="rounded-xl bg-white border-[#EAE3DC]" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[#5C534E]">Password * <span className="text-[#8C7F78] font-normal">(per accedere in futuro)</span></Label>
              <Input data-testid="onboarding-password" type="password" value={form.password}
                onChange={(e) => set("password", e.target.value)} placeholder="almeno 6 caratteri"
                className="rounded-xl bg-white border-[#EAE3DC]" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[#5C534E]">WhatsApp * <span className="text-[#8C7F78] font-normal">(per finalizzare l'acquisto)</span></Label>
              <Input data-testid="onboarding-whatsapp" type="tel" value={form.whatsapp}
                onChange={(e) => set("whatsapp", e.target.value)} placeholder="es. +39 333 1234567"
                className="rounded-xl bg-white border-[#EAE3DC]" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[#5C534E]">Città *</Label>
              <Input data-testid="onboarding-city" value={form.city}
                onChange={(e) => set("city", e.target.value)} placeholder="es. Napoli"
                className="rounded-xl bg-white border-[#EAE3DC]" />
            </div>

            <div className="grid grid-cols-2 gap-2 rounded-2xl bg-[#F4EFEA] p-1" data-testid="onboarding-born-toggle">
              {[[false, "Lo sto aspettando"], [true, "È già nato"]].map(([v, label]) => (
                <button key={label} type="button" data-testid={v ? "onboarding-born-yes" : "onboarding-born-no"}
                  onClick={() => setForm((f) => ({ ...f, born: v, year: String(yearsFor(v).includes(Number(f.year)) ? f.year : Y),
                    sex: v && f.sex === "sorpresa" ? "" : (!v && !f.sex ? "sorpresa" : f.sex) }))}
                  className={`rounded-xl h-10 text-sm font-medium transition-colors ${form.born === v ? "bg-white text-[#2D2825] shadow-sm" : "text-[#8C7F78]"}`}>
                  {label}
                </button>
              ))}
            </div>

            <div className="space-y-1.5">
              <Label className="text-[#5C534E]">{form.born ? "Data di nascita *" : "Data prevista della nascita *"}</Label>
              <div className="grid grid-cols-3 gap-3">
                <select data-testid="onboarding-day" value={form.day}
                  onChange={(e) => set("day", e.target.value)}
                  className="rounded-xl bg-white border border-[#EAE3DC] h-10 px-3 text-sm">
                  <option value="">Giorno</option>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                <select data-testid="onboarding-month" value={form.month}
                  onChange={(e) => set("month", e.target.value)}
                  className="rounded-xl bg-white border border-[#EAE3DC] h-10 px-3 text-sm capitalize">
                  {MONTHS_IT.map((m, i) => (
                    <option key={m} value={i + 1}>{m}</option>
                  ))}
                </select>
                <select data-testid="onboarding-year" value={form.year}
                  onChange={(e) => set("year", e.target.value)}
                  className="rounded-xl bg-white border border-[#EAE3DC] h-10 px-3 text-sm">
                  {yearsFor(form.born).map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
            <InfoPill show={!!(form.city || form.day)} testId="onboarding-pill-climate">
              nelle cliniche la temperatura media è di 20-22 °C anche in inverno, ma gli sbalzi termici richiedono cotone caldo e ciniglia.
            </InfoPill>

            <div className="space-y-1.5">
              <Label className="text-[#5C534E]">Sesso del bimbo</Label>
              <div className={`grid gap-3 ${form.born ? "grid-cols-2" : "grid-cols-3"}`}>
                {sexes.filter((s) => !form.born || s.v !== "sorpresa").map((s) => (
                  <button key={s.v} type="button" data-testid={`onboarding-sex-${s.v}`}
                    onClick={() => set("sex", s.v)}
                    className="rounded-xl h-10 text-sm font-medium border transition-all"
                    style={{
                      background: form.sex === s.v ? s.bg : "#fff",
                      borderColor: form.sex === s.v ? s.color : "#EAE3DC",
                      color: form.sex === s.v ? s.color : "#8C7F78",
                    }}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[#5C534E]">Nome del bimbo {!form.born && <span className="text-[#8C7F78] font-normal">(se l'avete già scelto)</span>}</Label>
              <Input data-testid="onboarding-baby-name" value={form.baby_name} maxLength={40}
                onChange={(e) => set("baby_name", e.target.value)} placeholder="es. Sofia"
                className="rounded-xl bg-white border-[#EAE3DC]" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[#5C534E]">{form.born ? "Peso alla nascita (kg)" : "Peso stimato (kg)"}</Label>
                <Input data-testid="onboarding-weight" type="number" step="0.1" value={form.est_weight}
                  onChange={(e) => set("est_weight", e.target.value)} placeholder="facoltativo"
                  className="rounded-xl bg-white border-[#EAE3DC]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#5C534E]">{form.born ? "Lunghezza alla nascita (cm)" : "Lunghezza stimata (cm)"}</Label>
                <Input data-testid="onboarding-length" type="number" step="0.1" value={form.est_length}
                  onChange={(e) => set("est_length", e.target.value)} placeholder="facoltativo"
                  className="rounded-xl bg-white border-[#EAE3DC]" />
              </div>
            </div>
            {form.born && (
              <div className="grid grid-cols-2 gap-3" data-testid="onboarding-current-size">
                <div className="space-y-1.5">
                  <Label className="text-[#5C534E]">Peso di oggi (kg)</Label>
                  <Input data-testid="onboarding-cur-weight" type="number" step="0.1" value={form.cur_weight}
                    onChange={(e) => set("cur_weight", e.target.value)} placeholder="facoltativo"
                    className="rounded-xl bg-white border-[#EAE3DC]" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#5C534E]">Lunghezza di oggi (cm)</Label>
                  <Input data-testid="onboarding-cur-length" type="number" step="0.1" value={form.cur_length}
                    onChange={(e) => set("cur_length", e.target.value)} placeholder="facoltativo"
                    className="rounded-xl bg-white border-[#EAE3DC]" />
                </div>
              </div>
            )}
            <InfoPill show={form.born && !!(form.cur_weight || form.cur_length)} testId="onboarding-pill-born">
              con peso e lunghezza di oggi calcoliamo la taglia giusta adesso e quella dei prossimi mesi, seguendo le curve di crescita OMS.
            </InfoPill>
            <InfoPill show={!form.born && !!(form.est_weight || form.est_length)} testId="onboarding-pill-size">
              le ecografie hanno un margine di errore del 10%: la taglia 0-1 mese è pensata proprio per vestire bene sia i bimbi da 2,8 kg che quelli da 3,5 kg.
            </InfoPill>

            <Button data-testid="onboarding-submit-button" type="submit" disabled={loading}
              className="w-full rounded-full bg-[#E5A8A0] hover:bg-[#C87D75] text-white h-12 text-base">
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Crea il mio profilo"}
            </Button>
            <p className="text-center text-xs text-[#8C7F78]">{form.born ? "Peso e lunghezza sono facoltativi, ma ci aiutano a consigliarti la taglia giusta." : "I dati stimati dalla ginecologa sono facoltativi."}</p>
          </form>
        ) : (
          <div className="space-y-4 mt-3">
            {resetMode ? (
              <form onSubmit={submitReset} className="space-y-4" data-testid="reset-form">
                <p className="text-sm text-[#8C7F78]">
                  Inserisci la tua email: ti invieremo un codice per scegliere una nuova password.
                </p>
                <div className="space-y-1.5">
                  <Label className="text-[#5C534E]">Email</Label>
                  <Input data-testid="reset-email" type="email" value={login.email}
                    onChange={(e) => setLogin((l) => ({ ...l, email: e.target.value }))} placeholder="nome@email.it"
                    className="rounded-xl bg-white border-[#EAE3DC]" />
                </div>
                {!resetSent ? (
                  <Button type="button" data-testid="send-reset-button" onClick={requestReset} disabled={loading}
                    className="w-full rounded-full bg-[#2D2825] hover:bg-[#473f3a] text-white h-12">
                    {loading ? <Loader2 className="animate-spin" size={18} /> : "Inviami il codice"}
                  </Button>
                ) : (
                  <>
                    <div className="space-y-1.5">
                      <Label className="text-[#5C534E]">Codice ricevuto via email</Label>
                      <Input data-testid="reset-code-input" value={resetCode} inputMode="numeric"
                        onChange={(e) => setResetCode(e.target.value)} placeholder="es. 123456"
                        className="rounded-xl bg-white border-[#EAE3DC] text-center tracking-[6px] text-lg" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[#5C534E]">Nuova password</Label>
                      <Input data-testid="reset-new-password" type="password" value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)} placeholder="almeno 6 caratteri"
                        className="rounded-xl bg-white border-[#EAE3DC]" />
                    </div>
                    <Button type="submit" data-testid="reset-submit-button" disabled={loading}
                      className="w-full rounded-full bg-[#E5A8A0] hover:bg-[#C87D75] text-white h-12">
                      {loading ? <Loader2 className="animate-spin" size={18} /> : "Reimposta password"}
                    </Button>
                    <button type="button" onClick={requestReset} disabled={loading}
                      className="w-full text-center text-sm text-[#8C7F78] hover:text-[#2D2825] underline">
                      Invia di nuovo il codice
                    </button>
                  </>
                )}
                <button type="button" data-testid="back-to-login-button" onClick={() => { setResetMode(false); setResetSent(false); }}
                  className="w-full text-center text-sm text-[#4E7363] hover:text-[#3d5a4e] underline">
                  Torna all'accesso
                </button>
              </form>
            ) : !codeMode ? (
              <form onSubmit={submitLogin} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-[#5C534E]">Email</Label>
                  <Input data-testid="login-email" type="email" value={login.email}
                    onChange={(e) => setLogin((l) => ({ ...l, email: e.target.value }))} placeholder="nome@email.it"
                    className="rounded-xl bg-white border-[#EAE3DC]" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#5C534E]">Password</Label>
                  <Input data-testid="login-password" type="password" value={login.password}
                    onChange={(e) => setLogin((l) => ({ ...l, password: e.target.value }))} placeholder="la tua password"
                    className="rounded-xl bg-white border-[#EAE3DC]" />
                </div>
                <Button data-testid="login-submit-button" type="submit" disabled={loading}
                  className="w-full rounded-full bg-[#E5A8A0] hover:bg-[#C87D75] text-white h-12 text-base">
                  {loading ? <Loader2 className="animate-spin" size={18} /> : "Accedi"}
                </Button>
                <button type="button" data-testid="use-code-button" onClick={() => { setCodeMode(true); setCodeSent(false); }}
                  className="w-full text-center text-sm text-[#4E7363] hover:text-[#3d5a4e] underline">
                  Accedi con un codice via email
                </button>
                <button type="button" data-testid="forgot-password-button" onClick={() => { setResetMode(true); setResetSent(false); }}
                  className="w-full text-center text-sm text-[#8C7F78] hover:text-[#2D2825] underline">
                  Password dimenticata?
                </button>
              </form>
            ) : (
              <form onSubmit={verifyCode} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-[#5C534E]">Email</Label>
                  <Input data-testid="code-email" type="email" value={login.email}
                    onChange={(e) => setLogin((l) => ({ ...l, email: e.target.value }))} placeholder="nome@email.it"
                    className="rounded-xl bg-white border-[#EAE3DC]" />
                </div>
                {!codeSent ? (
                  <Button type="button" data-testid="send-code-button" onClick={requestCode} disabled={loading}
                    className="w-full rounded-full bg-[#2D2825] hover:bg-[#473f3a] text-white h-12">
                    {loading ? <Loader2 className="animate-spin" size={18} /> : "Inviami il codice"}
                  </Button>
                ) : (
                  <>
                    <div className="space-y-1.5">
                      <Label className="text-[#5C534E]">Codice ricevuto via email</Label>
                      <Input data-testid="code-input" value={code} inputMode="numeric"
                        onChange={(e) => setCode(e.target.value)} placeholder="es. 123456"
                        className="rounded-xl bg-white border-[#EAE3DC] text-center tracking-[6px] text-lg" />
                    </div>
                    <Button type="submit" data-testid="verify-code-button" disabled={loading}
                      className="w-full rounded-full bg-[#E5A8A0] hover:bg-[#C87D75] text-white h-12">
                      {loading ? <Loader2 className="animate-spin" size={18} /> : "Accedi"}
                    </Button>
                    <button type="button" onClick={requestCode} disabled={loading}
                      className="w-full text-center text-sm text-[#8C7F78] hover:text-[#2D2825] underline">
                      Invia di nuovo il codice
                    </button>
                  </>
                )}
                <button type="button" data-testid="use-password-button" onClick={() => setCodeMode(false)}
                  className="w-full text-center text-sm text-[#4E7363] hover:text-[#3d5a4e] underline">
                  Accedi con email e password
                </button>
              </form>
            )}
          </div>
        )}
        <div className="pt-2 text-center">
          <Link to="/admin" data-testid="onboarding-admin-link"
            className="inline-flex items-center gap-1.5 text-xs text-[#8C7F78] hover:text-[#2D2825] transition-colors">
            <LayoutDashboard size={13} /> Sei lo shop? Accedi alla dashboard
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}
